import * as yup from "yup";
import { InferType } from "yup";

// Provider specialty options based on requirements
export const providerSpecialtyOptions = [
  "Allergy and immunology",
  "Anesthesiology",
  "Dermatology",
  "Diagnostic radiology",
  "Emergency medicine",
  "Family medicine",
  "Internal medicine",
  "Medical genetics",
  "Neurology",
  "Nuclear medicine",
  "Obstetrics and gynecology",
  "Ophthalmology",
  "Pathology",
  "Pediatrics",
  "Physical medicine and rehabilitation",
  "Preventive medicine",
  "Psychiatry",
  "Radiation oncology",
  "Surgery",
  "Urology",
] as const;

export type ProviderSpecialtyType = (typeof providerSpecialtyOptions)[number];

// ASCII printable characters validation (space to tilde)
const asciiPrintableRegex = /^[ -~]*$/;

// User profile validation schemas
export const userProfileSchema = yup.object().shape({
  // Read-only fields (no validation needed for editing)
  id: yup.string().required(),
  email: yup.string().email().required(),

  // Editable fields
  profileImage: yup.mixed<File | string>().nullable().optional(),

  firstName: yup
    .string()
    .required("First name is required")
    .max(64, "First name must not exceed 64 characters")
    .matches(
      asciiPrintableRegex,
      "First name must contain only ASCII printable characters",
    ),

  middleName: yup
    .string()
    .max(64, "Middle name must not exceed 64 characters")
    .matches(
      asciiPrintableRegex,
      "Middle name must contain only ASCII printable characters",
    )
    .optional(),

  lastName: yup
    .string()
    .required("Last name is required")
    .max(64, "Last name must not exceed 64 characters")
    .matches(
      asciiPrintableRegex,
      "Last name must contain only ASCII printable characters",
    ),

  phoneNumber: yup
    .string()
    .matches(
      /^\d{0,15}$/,
      "Phone number must contain only digits and not exceed 15 digits",
    )
    .optional(),

  npi: yup
    .string()
    .required("NPI is required")
    .matches(
      /^\d{1,10}$/,
      "NPI must contain only digits and not exceed 10 digits",
    ),

  specialties: yup
    .array()
    .of(yup.string().oneOf(providerSpecialtyOptions))
    .min(1, "Please select at least one specialty")
    .required("Specialties are required"),

  isActive: yup.boolean().optional(),

  rxntClientId: yup
    .string()
    .max(255, "RXNT Client ID must not exceed 255 characters")
    .optional(),

  rxntClientSecret: yup
    .string()
    .max(255, "RXNT Client Secret must not exceed 255 characters")
    .optional(),

  rxntDoctorId: yup
    .string()
    .max(50, "RXNT Doctor ID must not exceed 50 characters")
    .optional(),
});

// Infer the type from the schema
export type UserProfileFormData = InferType<typeof userProfileSchema>;
